import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import moment from 'moment';
import { Button } from '@mui/material';

import { FormatService } from '../../services/formatService';

import { IShipmentSearchRequest, IShipmentSearchResult } from '../../types/shipment';

import { Icons } from '../icons';
import { AppConfig } from '../../config/appConfig';
import Spinner from '../spinner';

export interface IShipmentTableProps {
	data?: IShipmentSearchResult;
	bol?: string;
	customer?: string;
	vendor?: string;
	status?: string;
	batchStatus?: string;
	sortField?: string;
	sortAscending?: boolean;
	isAwaitingResultSet?: boolean;
	userHasSearched?: boolean;
	update?: Function;
}

const ShipmentTable: React.FunctionComponent<IShipmentTableProps> = ({ data, bol, customer, vendor, status, batchStatus, sortField, sortAscending, isAwaitingResultSet, userHasSearched, update }) => {
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [searchRequest, setSearchRequest] = useState<IShipmentSearchRequest>({
		pageIndex: 0,
		pageSize: pageSize,
		bol: bol,
		customer: customer,
		vendor: vendor,
		status: status,
		batchStatus: batchStatus,
		sortField: sortField,
		sortAscending: sortAscending,
		searchId: null
	});

	useEffect(() => {
		setPageIndex(searchRequest.pageIndex);
		setPageSize(searchRequest.pageSize);

		if (userHasSearched) {
			update(searchRequest);
		}
	}, [searchRequest]);

	useEffect(() => {
		setPageIndex(0);
	}, [bol, customer, vendor, status, batchStatus])

	const handleHeaderClick = (field: string) => {
		if (isAwaitingResultSet || !userHasSearched) return;
		const request = { ...searchRequest };

		if (sortField == field) {
			request.sortAscending = !sortAscending;
		}
		else {
			request.sortAscending = true;
			request.sortField = field;
		}
		request.pageIndex = 0;

		setSearchRequest(request);
	}

	const handlePageBack = () => {
		if (pageIndex > 0) {
			const request = { ...searchRequest, ['pageIndex']: pageIndex - 1 }
			setSearchRequest(request);
		}
	}

	const handlePageForward = () => {
		if (pageIndex < (Math.floor(data?.totalMatchingRecords / pageSize))) {
			const request = { ...searchRequest, ['pageIndex']: pageIndex + 1 };
			setSearchRequest(request);
		}
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-bol">
					<p onClick={() => handleHeaderClick("bol")}>
						BOL#
						{" "}
						{(sortField == "bol" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "bol" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-customer">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-vendor">
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-plant-weight">
					<p onClick={() => handleHeaderClick("plantweight")}>
						Plant Wt
						{" "}
						{(sortField == "plantweight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "plantweight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-vendor-weight">
					<p onClick={() => handleHeaderClick("vendorweight")}>
						Vendor Wt
						{" "}
						{(sortField == "vendorweight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendorweight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-pickup">
					<p onClick={() => handleHeaderClick("pickupdate")}>
						Shipped
						{" "}
						{(sortField == "pickupdate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "pickupdate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-batch-status">
					<p onClick={() => handleHeaderClick("batchStatus")}>
						Batch
						{" "}
						{(sortField == "batchStatus" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "batchStatus" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<div className="data">
				<Spinner dependency={isAwaitingResultSet} text="Loading Shipments...">
					{data?.records.length > 0 ?
						(
							data?.records.map((item, index) => (
								<div className={"row"} key={item.id}>
									<div className="col-bol">
										<p>
											{item.bolNumber + " "}
											{item.isLegacy &&
												<Icons.LegacyData fill="#A84825" height="12px"></Icons.LegacyData>
											}
										</p>
									</div>
									<div className="col-customer"><p>{item.customerName}</p></div>
									<div className="col-vendor"><p>{item.vendorName}</p></div>
									<div className="col-plant-weight"><p>{FormatService.weight(item.plantWeightLbs)}</p></div>
									<div className="col-vendor-weight"><p>{FormatService.weight(item.vendorWeightLbs)}</p></div>
									<div className="col-pickup"><p>{item.pickupDate ? moment(item.pickupDate).format("MM/DD/yyyy") : ""}</p></div>
									<div className="col-status"><p>{item.status}</p></div>
									<div className="col-batch-status">
										<p>
											{item.isBatched ? (
												<NavLink to={"/batch/" + item.batchId}>
													<Button>
														<Icons.ViewItem fill="#434D76" height="16px"></Icons.ViewItem> <span>{item.batchId}</span>
													</Button>
												</NavLink>
											) : (
												<>-</>
											)}
										</p>
									</div>
									<div className="col-controls">
										<p>
											<NavLink to={"/shipment/" + item.id}>
												<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
												{" "}
												Open
											</NavLink>
										</p>
									</div>
								</div>
							))
						) :
						(
							<div className="no-results">{userHasSearched ? '0 results' : 'Please enter search criteria or a BOL, Invoice, or Manifest #'}</div>
						)
					}
				</Spinner>
			</div>
			<div className="pager">
				{data?.records.length > 0 &&
					<>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || pageIndex <= 0} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(pageIndex * pageSize) + 1} to ${Math.min((pageIndex * pageSize) + pageSize, data?.totalMatchingRecords)} of ${data?.totalMatchingRecords}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || ((pageIndex + 1) * pageSize) >= data?.totalMatchingRecords} onClick={handlePageForward}>Next</Button></div>
					</>
				}
			</div>
		</div>
	)
}

export default ShipmentTable;